import { Injectable, signal, computed, OnDestroy, DestroyRef, inject } from '@angular/core';
import { NotificationToastService } from '../../../features/notifications/services/notification-toast.service';
import { NotificationType } from '../../../domains/notification/models/notification.model';

export interface Toast {
  id: string;
  title?: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration: number;
  dismissible: boolean;
  action?: {
    label: string;
    callback: () => void;
  };
  horizontalPosition?: 'start' | 'center' | 'end';
  verticalPosition?: 'top' | 'bottom';
}

@Injectable({ providedIn: 'root' })
export class ToastService implements OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly notificationToast = inject(NotificationToastService, { optional: true });
  private readonly activeTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly toastsSignal = signal<Toast[]>([]);

  readonly toasts = computed(() => this.toastsSignal());

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.clearAllTimeouts();
    });
  }

  show(message: string, type: Toast['type'] = 'info', options?: Partial<Toast>): void {
    const id = this.generateId();
    const toast: Toast = {
      id,
      message,
      type,
      duration: 5000,
      dismissible: true,
      ...options,
    };

    this.toastsSignal.update((toasts) => [...toasts, toast]);

    if (this.notificationToast) {
      const colorMap: Record<Toast['type'], string> = {
        success: '#1FAD66',
        error: '#E5484D',
        warning: '#E89A2B',
        info: '#674CA5',
      };
      const titleMap: Record<Toast['type'], string> = {
        success: 'Success',
        error: 'Notice',
        warning: 'Notice',
        info: 'Notice',
      };
      this.notificationToast.show({
        id: `toast-${id}`,
        title: options?.title || titleMap[type] || 'Notice',
        body: message,
        color: colorMap[type] || '#674CA5',
        icon: type === 'success' ? 'star' : 'bell',
        isRead: false,
        createdAt: new Date().toISOString(),
        timeAgo: 'Just now',
        metadata: {},
        priority: 2,
        type: NotificationType.SystemAnnouncement,
        typeKey: 'system_announcement',
      });
    }

    if (toast.duration > 0) {
      this.scheduleAutoDismiss(id, toast.duration);
    }
  }

  success(message: string, options?: Partial<Toast>): void {
    this.show(message, 'success', options);
  }

  error(message: string, options?: Partial<Toast>): void {
    this.show(message, 'error', { ...options, duration: options?.duration ?? 8000 });
  }

  warning(message: string, options?: Partial<Toast>): void {
    this.show(message, 'warning', options);
  }

  info(message: string, options?: Partial<Toast>): void {
    this.show(message, 'info', options);
  }

  dismiss(id: string): void {
    this.cancelTimeout(id);
    this.toastsSignal.update((toasts) => toasts.filter((t) => t.id !== id));
  }

  clear(): void {
    this.clearAllTimeouts();
    this.toastsSignal.set([]);
  }

  dismissAll(): void {
    this.clear();
  }

  private scheduleAutoDismiss(id: string, duration: number): void {
    const timeoutId = setTimeout(() => {
      this.dismiss(id);
      this.activeTimeouts.delete(id);
    }, duration);
    this.activeTimeouts.set(id, timeoutId);
  }

  private cancelTimeout(id: string): void {
    const timeoutId = this.activeTimeouts.get(id);
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
      this.activeTimeouts.delete(id);
    }
  }

  private clearAllTimeouts(): void {
    this.activeTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.activeTimeouts.clear();
  }

  private generateId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  ngOnDestroy(): void {
    this.clearAllTimeouts();
  }
}

@Injectable({ providedIn: 'root' })
export class SnackbarService implements OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly configSignal = signal<{
    message: string;
    action?: string;
    duration?: number;
  } | null>(null);
  private readonly isOpenSignal = signal(false);
  private closeTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private resetTimeoutId: ReturnType<typeof setTimeout> | null = null;

  readonly config = this.configSignal.asReadonly();
  readonly isOpen = this.isOpenSignal.asReadonly();

  constructor(private readonly toastService: ToastService) {
    this.destroyRef.onDestroy(() => {
      this.clearAllTimeouts();
    });
  }

  open(config: { message: string; action?: string; duration?: number }): void {
    this.clearAllTimeouts();

    this.configSignal.set(config);
    this.isOpenSignal.set(true);

    const duration = config.duration ?? 3000;
    if (duration > 0) {
      this.closeTimeoutId = setTimeout(() => {
        this.close();
      }, duration);
    }
  }

  close(): void {
    this.isOpenSignal.set(false);
    this.resetTimeoutId = setTimeout(() => {
      this.configSignal.set(null);
    }, 200);
  }

  private clearAllTimeouts(): void {
    if (this.closeTimeoutId !== null) {
      clearTimeout(this.closeTimeoutId);
      this.closeTimeoutId = null;
    }
    if (this.resetTimeoutId !== null) {
      clearTimeout(this.resetTimeoutId);
      this.resetTimeoutId = null;
    }
  }

  ngOnDestroy(): void {
    this.clearAllTimeouts();
  }
}
